'use client';

import { QUERY_KEYS as CREDIT_QUERY_KEYS } from '@/@credit-consumption/types/constants';
import { useSubsidiaryOrgIds } from '@/@subsidiary/hooks/useSubsidiaryOrgIds';
import { ensureViewAsFresh, recordViewAsAction } from '@/@subsidiary/view-as-lifecycle';
import { getViewAsSnapshot, hasViewAsSession } from '@/@subsidiary/view-as-store';
import { EVENTS, tracking } from '@/lib/features/tracking';
import { useSession } from '@/providers/enhanced-session';
import type { QueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    AgentModes,
    CHAT_STATES,
    DEFAULT_MESSAGES,
    DispatchType,
    QUERY_KEYS,
    ROLE_TYPES,
    STATUS_MESSAGES,
    STREAMING_CONSTANTS,
    STREAMING_EVENT_TYPES,
} from '../constants';
import { type AnswerOutcome, answerKind, startAnswerTimer } from '../utils/answer-timing';
import {
    ContentModerationError,
    CreditLimitError,
    GenerationInProgressError,
    studioAIService,
    UsageLimitError,
} from '../services';
import type {
    ChatMessage,
    ChatMessageContent,
    ChatRequest,
    CreditGateData,
    FreemiumLimitData,
    StreamingEvent,
    StreamingEventHandlers,
    StreamingHandlers,
    StreamingState,
    StudioAIState,
} from '../types';
import {
    appendAgentOptionsContent,
    createStreamingState,
    createUserMessage,
    DocumentAttachmentInfo,
    handleChartEvent,
    handleContentArrayEvent,
    handleDeltaContentEvent,
    handleDoneEvent,
    handleErrorEvent,
    handleMetadataEvent,
    handlePlanProgressEvent,
    handleStatusEvent,
    handleStreamingComplete,
    showNoAnswerNotice,
    handleStructuredContentEvent,
    handleTextEvent,
    initializeAIMessage,
} from '../utils';
import {
    assertSendMatchesViewAs,
    daniConversationDetail,
    isNoConnectionProfileMessage,
    viewAsChatDenial,
    ViewAsDaniUnavailableError,
    type ViewAsSendGuardState,
} from '../utils/view-as-dani';
import { withReelsTurnRecording } from '../reels/record-turns';
import type { StudioAIAction } from './useChatReducer';

const NO_VIEW_AS_GUARD: { readonly current: ViewAsSendGuardState } = {
    current: { mismatch: null, groupSfAccountIds: [] },
};

export function useChatStreaming(config: {
    dispatch: React.Dispatch<StudioAIAction>;
    state: StudioAIState;
    queryClient: QueryClient;
    masqueradeClientRef: React.RefObject<{ id: string; name: string } | null>;
    /**
     * Staff View-as (M4S-5670 §4): the open conversation's mismatch lock and the
     * View-as client's group, read by the send guard. Absent = no lock.
     */
    viewAsGuardRef?: { readonly current: ViewAsSendGuardState };
    currentConversationIdRef: { readonly current: string | null };
    chatEndpointOverride: string | undefined;
    shouldUseFreemiumChat: boolean;
    setCurrentConversationId: (id: string | null) => void;
    /**
     * Marks a conversation as started by this client, so owner-only UI (Share)
     * works without waiting for a `getConversationHistory` round trip that the
     * URL-sync effect skips for a self-created chat. No-ops when the API has
     * already ruled on the conversation. See BT-2010.
     */
    claimConversationOwnership: (conversationId: string) => void;
    setPlannerRunId: (id: string | null) => void;
    setPlannerPhase: (phase: 'report' | null) => void;
    onFreemiumLimitReached: (data: FreemiumLimitData) => void;
    onCreditGatePaused: (data: CreditGateData) => void;
    onAgentOptions?: (payload: import('../types').AgentOptionsPayload) => void;
    /**
     * Fired the moment the agent enters report-generation phase, signalled by
     * a `thread_metadata` SSE event with `phase: 'report'` (emitted up-front
     * by `workers/planner/builder.generate_report_html`). Lets the kickoff
     * handler add the canned "your report is being generated" message and
     * start /status polling for text-driven kickoffs ("build that report"),
     * matching the explicit "Build a Report from this" button path.
     */
    onReportGenerationStarted?: (runId: string, brandLabel?: string) => void;
    /**
     * Fired when `report_updated` SSE arrives with a reportId. Used as a
     * secondary navigation trigger for flows where `thread_metadata
     * {phase:'report'}` may not arrive (e.g. text-driven crosstab flows).
     */
    onReportReady?: (reportId: number) => void;
    /** Called when POST /chat returns 409 (a generation is already in progress for this conversation) */
    onGenerationInProgress?: (conversationId: string) => void;
    /** Called when the user stops an in-progress generation, to cancel any active polling loop */
    onStopPolling?: () => void;
}) {
    const {
        dispatch,
        state,
        queryClient,
        masqueradeClientRef,
        viewAsGuardRef = NO_VIEW_AS_GUARD,
        currentConversationIdRef,
        chatEndpointOverride,
        shouldUseFreemiumChat,
        setCurrentConversationId,
        claimConversationOwnership,
        setPlannerRunId,
        setPlannerPhase,
        onFreemiumLimitReached,
        onCreditGatePaused,
        onAgentOptions,
        onReportGenerationStarted,
        onReportReady,
        onGenerationInProgress,
        onStopPolling,
    } = config;

    const { data: session } = useSession();
    const hasSubsidiaryAccess = session?.user?.hasSubsidiaryAccess === true;
    const subsidiaryOrgIdsFromFilter = useSubsidiaryOrgIds();
    const subsidiaryOrgIdsRef = useRef<number[]>([]);
    subsidiaryOrgIdsRef.current = hasSubsidiaryAccess ? subsidiaryOrgIdsFromFilter : [];

    const abortControllerRef = useRef<AbortController | null>(null);
    /** Ends the in-flight answer's timing; a stop or leaving the page reports it as stopped. */
    const finishAnswerTimingRef = useRef<((outcome: AnswerOutcome) => void) | null>(null);

    const streamingStateRef = useRef<StreamingState | null>(null);
    const mutationResolveRef = useRef<(() => void) | null>(null);
    const messagesRef = useRef(state.messages);

    useEffect(() => {
        messagesRef.current = state.messages;
    }, [state.messages]);

    useEffect(() => {
        return () => {
            finishAnswerTimingRef.current?.('stopped');
            abortControllerRef.current?.abort();
        };
    }, []);

    const duplicateMessagesRef = useRef<
        Array<{ role: 'user' | 'assistant'; content: Array<{ type: string; text: string }> }>
    >([]);

    /** Conversations this tab has already recorded a `dani_conversation` row for. */
    const viewAsRecordedRef = useRef<Set<string>>(new Set());

    const chatMutation = useMutation({
        mutationFn: async (request: ChatRequest) => {
            // The client this send goes as: the request's own, else DANI's
            // (under View-as, always the View-as client).
            const masqueradeOrgId = request.masqueradeOrgId || masqueradeClientRef.current?.id;
            const masqueradeOrgName =
                request.masqueradeOrgName || masqueradeClientRef.current?.name;

            // The View-as send guard (M4S-5670 §4), the one choke point for all
            // four DANI mounts. A refusal rejects like any failed send, so the
            // chat error state and the send handlers show it once; it shows no
            // toast of its own, and it never ends View-as itself.
            const viewAsSession = hasViewAsSession();
            if (viewAsSession || viewAsGuardRef.current.mismatch) {
                const fresh = viewAsSession ? await ensureViewAsFresh() : true;
                assertSendMatchesViewAs({
                    session: getViewAsSnapshot(),
                    fresh,
                    mismatch: viewAsGuardRef.current.mismatch,
                    masqueradeOrgId,
                    groupSfAccountIds: viewAsGuardRef.current.groupSfAccountIds,
                    plannerRequested:
                        request.agentMode === AgentModes.PLANNER ||
                        viewAsGuardRef.current.plannerSelected === true,
                    plannerAllowedForClient: viewAsGuardRef.current.plannerAllowedForClient,
                });
            }
            const viewAsClientName = viewAsSession ? (masqueradeOrgName ?? null) : null;
            const recordConversation = () => {
                const id = request.conversationId;
                if (!viewAsSession || !id || viewAsRecordedRef.current.has(id)) return;
                viewAsRecordedRef.current.add(id);
                recordViewAsAction(
                    'dani_conversation',
                    id,
                    daniConversationDetail({
                        masqueradeOrgId,
                        sessionSfAccountId: getViewAsSnapshot().sfAccountId,
                        isPlanner: request.agentMode === AgentModes.PLANNER,
                    }),
                );
            };

            return new Promise<void>((resolve, reject) => {
                const conversationId = request.conversationId ?? null;
                const isNewConversation = !state.currentThreadId;

                const streamingState = createStreamingState();
                streamingState.conversationId = conversationId;
                streamingState.isNewConversation = isNewConversation;
                streamingState.isPlannerMode = request.agentMode === AgentModes.PLANNER;

                streamingStateRef.current = streamingState;
                mutationResolveRef.current = resolve;

                // How long this answer takes, reported once when it ends.
                const answerTimer = startAnswerTimer(
                    answerKind({
                        isPlanner: request.agentMode === AgentModes.PLANNER,
                        isExperimental: request.agentMode === AgentModes.EXPERIMENTAL,
                        silent: request.silent,
                    }),
                    isNewConversation,
                    () => performance.now(),
                );
                const finishAnswerTiming = (outcome: AnswerOutcome) => {
                    const timing = answerTimer.finish(outcome);
                    if (finishAnswerTimingRef.current === finishAnswerTiming) {
                        finishAnswerTimingRef.current = null;
                    }
                    if (!timing) return;
                    tracking.metric(EVENTS.PERFORMANCE.DANI_ANSWER_TIME, timing.totalMs, {
                        ...timing,
                        conversationId,
                    });
                };
                finishAnswerTimingRef.current = finishAnswerTiming;

                // Accumulates fields from thread_metadata events across multiple
                // SSE messages. The backend sometimes sends runId and phase in
                // separate events rather than a single one, so we combine them
                // here and fire the kickoff once we have both.
                const accumulatedMeta: { runId?: string; phase?: string } = {};
                let kickoffFired = false;

                const timeoutCallback = () => {
                    if (!streamingState.isResolved) {
                        finishAnswerTiming('no_answer');
                        showNoAnswerNotice(eventHandlers);
                        dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });
                        dispatch({ type: DispatchType.ClearError });
                        streamingState.isResolved = true;
                        resolve();
                    }
                };

                let timeoutId = setTimeout(timeoutCallback, STREAMING_CONSTANTS.TIMEOUT_MS);

                const resetIdleTimeout = () => {
                    if (streamingState.isResolved) return;
                    clearTimeout(timeoutId);
                    timeoutId = setTimeout(timeoutCallback, STREAMING_CONSTANTS.TIMEOUT_MS);
                    // Keep eventHandlers in sync so any handler that
                    // destructures `timeoutId` (e.g. handleDoneEvent,
                    // handleErrorEvent, handleStreamingComplete) clears
                    // the CURRENT timer, not the stale initial one.
                    eventHandlers.timeoutId = timeoutId;
                };

                const eventHandlers: StreamingEventHandlers = {
                    dispatch,
                    queryClient,
                    state,
                    request,
                    streamingState,
                    timeoutId,
                    resolve,
                    reject,
                    currentConversationIdRef,
                };

                const abortController = new AbortController();
                abortControllerRef.current = abortController;

                const streamHandlers: StreamingHandlers = {
                    onHeartbeat: () => {
                        resetIdleTimeout();
                    },
                    onMessage: (data: StreamingEvent) => {
                        resetIdleTimeout();
                        answerTimer.event(data.type);
                        // The API accepted the send: record it under View-as.
                        recordConversation();

                        const isStillActive =
                            (currentConversationIdRef.current ?? null) ===
                            (streamingState.conversationId ?? null);

                        if (process.env.NODE_ENV === 'development') {
                            console.log('[DANI SSE]', data.type, {
                                ...(data.runId != null && { runId: data.runId }),
                                ...(data.phase != null && { phase: data.phase }),
                                ...(data.reportId != null && { reportId: data.reportId }),
                                ...(data.threadId != null && { threadId: data.threadId }),
                                isStillActive,
                                convRef: currentConversationIdRef.current,
                                convStream: streamingState.conversationId,
                            });
                        }

                        if (data.type === STREAMING_EVENT_TYPES.THREAD_METADATA) {
                            // Accumulate fields across events for diagnostic logging.
                            if (data.runId) accumulatedMeta.runId = data.runId;
                            if (data.phase) accumulatedMeta.phase = data.phase;

                            if (process.env.NODE_ENV === 'development' && data.runId) {
                                console.log('[DANI kickoff?]', {
                                    isStillActive,
                                    kickoffFired,
                                    runId: accumulatedMeta.runId,
                                    phase: accumulatedMeta.phase ?? '(none)',
                                    willFire: isStillActive && !kickoffFired,
                                });
                            }

                            if (isStillActive) {
                                if (data.threadId) {
                                    dispatch({
                                        type: DispatchType.SetThreadId,
                                        payload: data.threadId,
                                    });
                                }
                                if (data.runId) {
                                    setPlannerRunId(data.runId);
                                }
                                if (data.phase) {
                                    setPlannerPhase(data.phase);
                                }

                                // Fire the kickoff as soon as we have a runId.
                                // Preferred: phase:'report' + runId together.
                                // Fallback: test1/staging backends send runId without
                                // phase, so treat any bare runId as a report kickoff.
                                if (
                                    !kickoffFired &&
                                    accumulatedMeta.runId &&
                                    (accumulatedMeta.phase === 'report' || !accumulatedMeta.phase)
                                ) {
                                    kickoffFired = true;
                                    onReportGenerationStarted?.(accumulatedMeta.runId);
                                }
                            }
                        }

                        if (isStillActive && !streamingState.currentAiMessage) {
                            initializeAIMessage(data, eventHandlers);
                        }

                        switch (data.type) {
                            case STREAMING_EVENT_TYPES.METADATA:
                                handleMetadataEvent(data, eventHandlers);
                                break;
                            case STREAMING_EVENT_TYPES.STATUS:
                                handleStatusEvent(data, eventHandlers);
                                break;
                            case STREAMING_EVENT_TYPES.DELTA_CONTENT:
                                handleDeltaContentEvent(data, eventHandlers);
                                break;
                            case STREAMING_EVENT_TYPES.CONTENT_ARRAY:
                                handleContentArrayEvent(data, eventHandlers);
                                break;
                            case STREAMING_EVENT_TYPES.TEXT:
                                handleTextEvent(data, eventHandlers);
                                break;
                            case STREAMING_EVENT_TYPES.CHART:
                                handleChartEvent(data, eventHandlers);
                                break;
                            case STREAMING_EVENT_TYPES.TABLE:
                                handleDeltaContentEvent(data, eventHandlers);
                                break;
                            case STREAMING_EVENT_TYPES.AGENT_OPTIONS:
                                if (data.agent_options) {
                                    onAgentOptions?.(data.agent_options);
                                    appendAgentOptionsContent(data.agent_options, eventHandlers);
                                }
                                break;
                            case STREAMING_EVENT_TYPES.PLAN_SUMMARY:
                                handleStructuredContentEvent(
                                    data,
                                    STREAMING_EVENT_TYPES.PLAN_SUMMARY,
                                    'plan_summary',
                                    eventHandlers,
                                );
                                break;
                            case STREAMING_EVENT_TYPES.PLAN_PROGRESS:
                                handlePlanProgressEvent(data, eventHandlers);
                                break;
                            case STREAMING_EVENT_TYPES.REPORT_UPDATED:
                                if (isStillActive) {
                                    if (data.reportId != null) {
                                        void queryClient.invalidateQueries({
                                            queryKey: QUERY_KEYS.REPORT_HTML(data.reportId),
                                        });
                                        onReportReady?.(data.reportId);
                                    }
                                    void queryClient.invalidateQueries({
                                        queryKey: ['studio-ai', 'reports'],
                                    });
                                }
                                break;
                            case STREAMING_EVENT_TYPES.DONE:
                                finishAnswerTiming('done');
                                handleDoneEvent(eventHandlers);
                                void queryClient.invalidateQueries({
                                    queryKey: CREDIT_QUERY_KEYS.SUMMARY(),
                                });
                                break;
                            case STREAMING_EVENT_TYPES.ERROR:
                                finishAnswerTiming('error');
                                // The agent's "no connection profile"
                                // refusal, under View-as, names the client.
                                handleErrorEvent(
                                    viewAsClientName &&
                                        isNoConnectionProfileMessage(data.message ?? data.content)
                                        ? {
                                              ...data,
                                              message: new ViewAsDaniUnavailableError(
                                                  viewAsClientName,
                                              ).message,
                                          }
                                        : data,
                                    eventHandlers,
                                );
                                break;
                        }
                    },
                    onError: (error: Error) => {
                        finishAnswerTiming(error.name === 'AbortError' ? 'stopped' : 'error');
                        if (error instanceof CreditLimitError) {
                            onCreditGatePaused({
                                reason: error.reason,
                                resetDate: error.resetDate,
                                licenseEndDate: error.licenseEndDate,
                                annualConsumed: error.annualConsumed,
                                annualPool: error.annualPool,
                                annualRemaining: error.annualRemaining,
                                userSpendLimit: error.userSpendLimit,
                                userMonthToDate: error.userMonthToDate,
                            });
                            void queryClient.invalidateQueries({
                                queryKey: CREDIT_QUERY_KEYS.SUMMARY(),
                            });
                            dispatch({
                                type: DispatchType.SetChatState,
                                payload: CHAT_STATES.IDLE,
                            });
                            dispatch({ type: DispatchType.SetCurrentStatus, payload: null });
                            dispatch({ type: DispatchType.SetLoading, payload: false });
                            clearTimeout(timeoutId);
                            if (!streamingState.isResolved) {
                                streamingState.isResolved = true;
                                resolve();
                            }
                            return;
                        }

                        if (error instanceof UsageLimitError && shouldUseFreemiumChat) {
                            onFreemiumLimitReached({
                                hasReachedLimit: true,
                                currentCount: error.currentCount ?? 0,
                                limit: error.limit ?? 0,
                                limitType: (error.limitType as 'message' | 'token') ?? 'message',
                                daysUntilReset: error.daysUntilReset ?? null,
                                limitMessage: error.limitMessage ?? null,
                            });
                            dispatch({
                                type: DispatchType.SetChatState,
                                payload: CHAT_STATES.IDLE,
                            });
                            dispatch({
                                type: DispatchType.SetCurrentStatus,
                                payload: null,
                            });
                            dispatch({
                                type: DispatchType.SetLoading,
                                payload: false,
                            });
                            clearTimeout(timeoutId);
                            if (!streamingState.isResolved) {
                                streamingState.isResolved = true;
                                resolve();
                            }
                            return;
                        }

                        if (error instanceof ContentModerationError) {
                            const moderationMessage: ChatMessage = {
                                id: `moderation-${Date.now()}`,
                                role: ROLE_TYPES.ASSISTANT,
                                content: [
                                    {
                                        type: STREAMING_EVENT_TYPES.TEXT,
                                        text: `Your message could not be processed because it was flagged by our content moderation system. If you believe this was an error, please try rephrasing your request.`,
                                    },
                                ],
                                timestamp: new Date(),
                                isStreaming: false,
                            };
                            dispatch({
                                type: DispatchType.AddMessage,
                                payload: moderationMessage,
                            });
                            dispatch({
                                type: DispatchType.SetChatState,
                                payload: CHAT_STATES.IDLE,
                            });
                            dispatch({
                                type: DispatchType.SetCurrentStatus,
                                payload: null,
                            });
                            dispatch({
                                type: DispatchType.SetLoading,
                                payload: false,
                            });
                            clearTimeout(timeoutId);
                            if (!streamingState.isResolved) {
                                streamingState.isResolved = true;
                                resolve();
                            }
                            return;
                        }

                        // Under View-as, a 403 that is not a credits or
                        // usage denial names the client ("<Client> can't
                        // use DANI: ..."). It never ends View-as.
                        const shown = viewAsChatDenial(error, viewAsClientName) ?? error;
                        streamingState.hasError = true;
                        dispatch({ type: DispatchType.SetError, payload: shown.message });
                        dispatch({
                            type: DispatchType.SetChatState,
                            payload: CHAT_STATES.ERROR,
                        });
                        dispatch({ type: DispatchType.SetCurrentStatus, payload: null });
                        clearTimeout(timeoutId);
                        if (!streamingState.isResolved) {
                            streamingState.isResolved = true;
                            reject(shown);
                        }
                    },
                    onComplete: () => {
                        finishAnswerTiming('done');
                        handleStreamingComplete(eventHandlers);
                    },
                };

                // A turn about a video study goes to the Reels agent, which
                // answers in the same event format (dani-plan.md §9.1).
                if (request.reels) {
                    void studioAIService.sendReelsChatMessage(
                        {
                            message: request.prompt,
                            conversationId,
                            study: request.reels.study,
                            history: request.reels.history,
                        },
                        withReelsTurnRecording(streamHandlers, conversationId, request.prompt),
                        abortController,
                    );
                    return;
                }

                studioAIService.sendChatMessage(
                    request.prompt,
                    request.threadId || null,
                    request.parentMessageId || state.currentParentMessageId || '0',
                    streamingState.conversationId,
                    streamHandlers,
                    abortController,
                    request.agentMode === AgentModes.PLANNER,
                    request.contextData,
                    request.previousMessages,
                    masqueradeOrgId,
                    masqueradeOrgName,
                    chatEndpointOverride,
                    request.agentMode === AgentModes.EXPERIMENTAL,
                    request.silent,
                    request.templateId,
                    request.subsidiaryOrgIds ?? subsidiaryOrgIdsRef.current,
                );
            });
        },
        onError: (error) => {
            // AbortError is thrown when the user navigates away while streaming.
            // In that case, stopConversation() already cleaned up state — don't overwrite
            // the new conversation's state with an error.
            if ((error as Error).name === 'AbortError') return;
            dispatch({ type: DispatchType.SetError, payload: error.message });
            dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.ERROR });
            dispatch({ type: DispatchType.SetCurrentStatus, payload: null });
        },
        onSettled: () => {
            dispatch({ type: DispatchType.SetLoading, payload: false });
            streamingStateRef.current = null;
            // Refresh org-trial / freemium remaining-quota footer after every send so a
            // user who just consumed their last allowance sees the limit reflected
            // before they can submit again.
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ACCESS_STATUS });
            mutationResolveRef.current = null;
        },
    });

    const sendSilentMessage = useCallback(
        async (request: ChatRequest) => {
            request.silent = true;

            if (request.conversationId) {
                setCurrentConversationId(request.conversationId);
                claimConversationOwnership(request.conversationId);
            }

            dispatch({ type: DispatchType.SetLoading, payload: true });
            dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.LOADING });
            dispatch({ type: DispatchType.ClearError });

            try {
                if (masqueradeClientRef.current) {
                    request.masqueradeOrgId = masqueradeClientRef.current.id;
                    request.masqueradeOrgName = masqueradeClientRef.current.name;
                }
                if (subsidiaryOrgIdsRef.current.length) {
                    request.subsidiaryOrgIds = subsidiaryOrgIdsRef.current;
                }

                const currentThreadId = state.currentThreadId;
                const currentParentMessageId = state.currentParentMessageId;

                if (request.threadId) {
                    // Caller-supplied threadId takes precedence
                } else if (currentThreadId) {
                    request.threadId = currentThreadId;
                    request.parentMessageId = currentParentMessageId || '0';
                } else {
                    request.threadId = '0';
                    request.parentMessageId = '0';
                }

                return chatMutation.mutateAsync(request);
            } catch (error) {
                dispatch({ type: DispatchType.SetError, payload: (error as Error).message });
                dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.ERROR });
                dispatch({ type: DispatchType.SetLoading, payload: false });
                throw error;
            }
        },
        [
            chatMutation,
            state.currentParentMessageId,
            state.currentThreadId,
            dispatch,
            masqueradeClientRef,
            setCurrentConversationId,
            claimConversationOwnership,
        ],
    );

    const sendMessage = useCallback(
        async (request: ChatRequest) => {
            if (request.conversationId) {
                setCurrentConversationId(request.conversationId);
                claimConversationOwnership(request.conversationId);
            }

            const documentAttachment = request.contextData?.attachedDocument as
                | DocumentAttachmentInfo
                | undefined;

            const displayMessage =
                (request.contextData?.displayMessage as string) || request.prompt;

            const userMessage = createUserMessage(displayMessage, undefined, documentAttachment);
            dispatch({ type: DispatchType.AddMessage, payload: userMessage });

            dispatch({ type: DispatchType.SetLoading, payload: true });
            dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.LOADING });
            dispatch({ type: DispatchType.ClearError });

            try {
                if (masqueradeClientRef.current) {
                    request.masqueradeOrgId = masqueradeClientRef.current.id;
                    request.masqueradeOrgName = masqueradeClientRef.current.name;
                }
                if (subsidiaryOrgIdsRef.current.length) {
                    request.subsidiaryOrgIds = subsidiaryOrgIdsRef.current;
                }

                const currentThreadId = state.currentThreadId;
                const currentParentMessageId = state.currentParentMessageId;

                if (request.threadId) {
                    // Caller-supplied threadId takes precedence
                } else if (currentThreadId) {
                    dispatch({
                        type: DispatchType.SetCurrentStatus,
                        payload: `${STATUS_MESSAGES.THINKING.message}|${STATUS_MESSAGES.THINKING.icon}`,
                    });
                    request.threadId = currentThreadId;
                    request.parentMessageId = currentParentMessageId || '0';
                } else {
                    dispatch({
                        type: DispatchType.SetCurrentStatus,
                        payload: `${STATUS_MESSAGES.CREATING_THREAD.message}|${STATUS_MESSAGES.CREATING_THREAD.icon}`,
                    });
                    request.threadId = '0';
                    request.parentMessageId = '0';

                    if (duplicateMessagesRef.current.length > 0) {
                        request.previousMessages = duplicateMessagesRef.current;
                        duplicateMessagesRef.current = [];
                    }
                }

                return chatMutation.mutateAsync(request);
            } catch (error) {
                if (error instanceof GenerationInProgressError) {
                    // Server is already streaming a response for this conversation.
                    // Remove the optimistically added user message and hand off to
                    // the lifecycle hook, which will re-fetch the conversation,
                    // detect generatingResponseAt, and start polling.
                    dispatch({ type: DispatchType.SetLoading, payload: false });
                    dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });
                    dispatch({ type: DispatchType.SetCurrentStatus, payload: null });
                    onGenerationInProgress?.(request.conversationId ?? '');
                    return;
                }
                dispatch({ type: DispatchType.SetError, payload: (error as Error).message });
                dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.ERROR });
                dispatch({ type: DispatchType.SetLoading, payload: false });
                throw error;
            }
        },
        [
            chatMutation,
            state.currentParentMessageId,
            state.currentThreadId,
            dispatch,
            masqueradeClientRef,
            setCurrentConversationId,
            claimConversationOwnership,
            onGenerationInProgress,
        ],
    );

    const stopConversation = useCallback(async () => {
        finishAnswerTimingRef.current?.('stopped');
        const inFlight = streamingStateRef.current;
        if (inFlight) {
            inFlight.isResolved = true;
        }
        const resolveInFlight = mutationResolveRef.current;
        if (resolveInFlight) {
            resolveInFlight();
            mutationResolveRef.current = null;
        }

        // Cancel any active polling loop (e.g. user stops while we're polling
        // after navigating back to a conversation with generatingResponseAt set).
        onStopPolling?.();

        // For regular chat streams, signal the server to abort server-side generation.
        // Plan-mode streams do not use the pre-save/lock mechanism, so skip the call.
        const conversationId = inFlight?.conversationId ?? currentConversationIdRef.current;
        const isPlannerStream = inFlight?.isPlannerMode ?? false;
        if (!isPlannerStream && conversationId) {
            // Fire-and-forget — we don't await so the UI resets immediately.
            studioAIService.stopConversation(conversationId).catch(() => undefined);
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        if (messagesRef.current.length > 0) {
            const lastMessage = messagesRef.current[messagesRef.current.length - 1];
            if (lastMessage.role === ROLE_TYPES.ASSISTANT && lastMessage.isStreaming) {
                const messageContent: ChatMessageContent[] = Array.isArray(lastMessage.content)
                    ? lastMessage.content
                    : [{ type: STREAMING_EVENT_TYPES.TEXT, text: lastMessage.content as string }];

                let updatedContent: ChatMessage['content'];

                const hasSubstantialTextContent = messageContent.some(
                    (content) =>
                        content.type === STREAMING_EVENT_TYPES.TEXT &&
                        content.text &&
                        content.text.trim().length > 10,
                );

                if (hasSubstantialTextContent) {
                    updatedContent = lastMessage.content;
                } else {
                    const nonTextContent = messageContent.filter(
                        (content) => content.type !== STREAMING_EVENT_TYPES.TEXT,
                    );
                    updatedContent = [
                        ...nonTextContent,
                        { type: STREAMING_EVENT_TYPES.TEXT, text: DEFAULT_MESSAGES.CANCELLED },
                    ];
                }

                dispatch({
                    type: DispatchType.UpdateMessage,
                    payload: {
                        messageId: lastMessage.id,
                        updates: {
                            content: updatedContent,
                            isStreaming: false,
                        },
                    },
                });
            }
        }

        dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });
        dispatch({ type: DispatchType.SetCurrentStatus, payload: null });
        dispatch({ type: DispatchType.SetLoading, payload: false });
        dispatch({ type: DispatchType.ClearError });
    }, [dispatch, currentConversationIdRef, onStopPolling]);

    return useMemo(
        () => ({
            chatMutation,
            sendMessage,
            sendSilentMessage,
            stopConversation,
            duplicateMessagesRef,
        }),
        [chatMutation, sendMessage, sendSilentMessage, stopConversation],
    );
}
